import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, ChevronLeft, MapPin, UserCheck } from "lucide-react";
import { toast } from "sonner";
import FileUploader from "@/components/FileUploader";

const EMPTY_SITE = {
  name: "", address_line1: "", address_line2: "", city: "", postcode: "",
  country: "United Kingdom", contact_name: "", contact_phone: "",
  service_requirements: "", notes: "", miles: "", routes: [],
};

const EMPTY_ASSIGN = {
  subcontractor_id: "", scope: "all", site_ids: [], is_default: false, notes: "",
};

export default function ClientDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [client, setClient] = useState(null);
  const [sites, setSites] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [subs, setSubs] = useState([]);

  // Site dialog state
  const [siteOpen, setSiteOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [siteForm, setSiteForm] = useState(EMPTY_SITE);

  // Assignment dialog state
  const [assignOpen, setAssignOpen] = useState(false);
  const [editingAssign, setEditingAssign] = useState(null);
  const [assignForm, setAssignForm] = useState(EMPTY_ASSIGN);

  const load = async () => {
    try {
      const [c, s, a, sc] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/clients/${id}/sites`),
        api.get(`/clients/${id}/assignments`),
        api.get("/contacts", { params: { type: "customer" } }),
      ]);
      setClient(c.data); setSites(s.data); setAssignments(a.data); setSubs(sc.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [id]);

  // Auto-open the Add Site dialog if redirected here from Clients with ?addSite=1
  useEffect(() => {
    if (searchParams.get("addSite") === "1") {
      setEditingSite(null);
      setSiteForm(EMPTY_SITE);
      setSiteOpen(true);
      // remove the param so refreshing doesn't keep re-opening it
      searchParams.delete("addSite");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // ----- Sites -----
  const openCreateSite = () => { setEditingSite(null); setSiteForm(EMPTY_SITE); setSiteOpen(true); };
  const openEditSite = (s) => { setEditingSite(s); setSiteForm({ ...EMPTY_SITE, ...s }); setSiteOpen(true); };
  const saveSite = async () => {
    if (!siteForm.name.trim()) return toast.error("Site name is required.");
    const cleanRoutes = (siteForm.routes || [])
      .filter((r) => (r.name || "").trim() || Number(r.miles) > 0 || (r.dropoff_address || "").trim())
      .map((r) => ({
        name: (r.name || "").trim(),
        miles: Number(r.miles) || 0,
        dropoff_address: (r.dropoff_address || "").trim(),
      }));
    const payload = { ...siteForm, miles: Number(siteForm.miles) || 0, routes: cleanRoutes };
    try {
      if (editingSite) await api.put(`/sites/${editingSite.id}`, payload);
      else await api.post(`/clients/${id}/sites`, payload);
      toast.success(`Site ${editingSite ? "updated" : "added"}.`);
      setSiteOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeSite = async (s) => {
    if (!window.confirm(`Delete site "${s.name}"? Any jobs already created at this site keep a copy of its details.`)) return;
    try { await api.delete(`/sites/${s.id}`); toast.success("Site removed."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // ----- Assignments -----
  const openCreateAssign = () => { setEditingAssign(null); setAssignForm(EMPTY_ASSIGN); setAssignOpen(true); };
  const openEditAssign = (a) => { setEditingAssign(a); setAssignForm({ ...EMPTY_ASSIGN, ...a }); setAssignOpen(true); };
  const saveAssign = async () => {
    if (!assignForm.subcontractor_id) return toast.error("Pick a subcontractor.");
    if (assignForm.scope === "specific" && assignForm.site_ids.length === 0) return toast.error("Select at least one site for specific scope.");
    try {
      if (editingAssign) await api.put(`/assignments/${editingAssign.id}`, assignForm);
      else await api.post(`/clients/${id}/assignments`, assignForm);
      toast.success(`Assignment ${editingAssign ? "updated" : "created"}.`);
      setAssignOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeAssign = async (a) => {
    if (!window.confirm("Remove this subcontractor assignment?")) return;
    try { await api.delete(`/assignments/${a.id}`); toast.success("Assignment removed."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleSiteInAssign = (sid) => {
    setAssignForm((f) => {
      const exists = f.site_ids.includes(sid);
      return { ...f, site_ids: exists ? f.site_ids.filter((x) => x !== sid) : [...f.site_ids, sid] };
    });
  };

  const subName = (sid) => subs.find((x) => x.id === sid)?.name || "—";
  const siteName = (sid) => sites.find((x) => x.id === sid)?.name || "—";

  if (!client) return <div className="text-slate-500 text-sm">Loading…</div>;

  return (
    <div data-testid="client-detail-page" className="space-y-6">
      <div>
        <Link to="/clients" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
          <ChevronLeft className="h-3 w-3" /> All clients
        </Link>
        <div className="flex items-baseline justify-between mt-2 gap-4 flex-wrap">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900">{client.name}</h1>
            {client.number && (
              <span className="font-mono text-xs font-semibold tracking-wider text-orange-700 bg-orange-50 border border-orange-200 rounded-sm px-2 py-1" data-testid="client-detail-number">
                {client.number}
              </span>
            )}
          </div>
          <div className="text-sm text-slate-500 tabular-nums">
            {sites.length} site{sites.length === 1 ? "" : "s"} · {assignments.length} subcontractor assignment{assignments.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="mt-2 text-sm text-slate-600">
          {client.email && <span>{client.email}</span>}
          {client.phone && <span className="ml-3">{client.phone}</span>}
          {client.city && <span className="ml-3">{client.city}</span>}
        </div>
        {(client.bank_name || client.bank_account_number || client.bank_sort_code) && (
          <div className="mt-3 inline-flex flex-wrap items-center gap-3 rounded-sm border border-slate-200 bg-slate-50/70 px-3 py-2 text-xs text-slate-700" data-testid="client-detail-bank">
            <span className="uppercase tracking-wider text-[10px] font-semibold text-slate-500">Payable to</span>
            {client.bank_name && <span><span className="text-slate-500">Bank</span> <span className="font-medium">{client.bank_name}</span></span>}
            {client.bank_account_number && <span><span className="text-slate-500">Acc</span> <span className="font-mono tabular-nums">{client.bank_account_number}</span></span>}
            {client.bank_sort_code && <span><span className="text-slate-500">Sort</span> <span className="font-mono tabular-nums">{client.bank_sort_code}</span></span>}
          </div>
        )}
      </div>

      <Tabs defaultValue="sites" className="w-full">
        <TabsList className="bg-slate-100 rounded-sm">
          <TabsTrigger data-testid="tab-sites" value="sites" className="rounded-sm">Sites</TabsTrigger>
          <TabsTrigger data-testid="tab-assignments" value="assignments" className="rounded-sm">Subcontractors</TabsTrigger>
        </TabsList>

        {/* SITES TAB */}
        <TabsContent value="sites" className="mt-4">
          <Card className="rounded-sm border border-slate-200 shadow-none">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="text-sm text-slate-600">Each client can have multiple delivery sites.</div>
              <Dialog open={siteOpen} onOpenChange={setSiteOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-site-btn" onClick={openCreateSite} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
                    <Plus className="h-4 w-4 mr-2" /> Add Site
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl rounded-sm">
                  <DialogHeader>
                    <DialogTitle>{editingSite ? "Edit Site" : "New Site"}</DialogTitle>
                  </DialogHeader>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
                    <FieldI label="Site name" testid="site-name" value={siteForm.name} onChange={(v) => setSiteForm({ ...siteForm, name: v })} required className="md:col-span-2" />
                    <FieldI label="Address line 1" testid="site-addr1" value={siteForm.address_line1} onChange={(v) => setSiteForm({ ...siteForm, address_line1: v })} className="md:col-span-2" />
                    <FieldI label="Address line 2" testid="site-addr2" value={siteForm.address_line2} onChange={(v) => setSiteForm({ ...siteForm, address_line2: v })} className="md:col-span-2" />
                    <FieldI label="City" testid="site-city" value={siteForm.city} onChange={(v) => setSiteForm({ ...siteForm, city: v })} />
                    <FieldI label="Postcode" testid="site-postcode" value={siteForm.postcode} onChange={(v) => setSiteForm({ ...siteForm, postcode: v })} />
                    <FieldI label="Site contact name" testid="site-contact" value={siteForm.contact_name} onChange={(v) => setSiteForm({ ...siteForm, contact_name: v })} />
                    <FieldI label="Site contact phone" testid="site-contact-phone" value={siteForm.contact_phone} onChange={(v) => setSiteForm({ ...siteForm, contact_phone: v })} />
                    <FieldI
                      label="Total miles"
                      testid="site-miles"
                      type="number"
                      step="0.1"
                      value={siteForm.miles}
                      onChange={(v) => setSiteForm({ ...siteForm, miles: v })}
                      className="md:col-span-2"
                    />
                    <div className="md:col-span-2">
                      <Label className="text-xs text-slate-600">Service requirements</Label>
                      <textarea
                        data-testid="site-service-req"
                        rows={3}
                        value={siteForm.service_requirements ?? ""}
                        onChange={(e) => setSiteForm({ ...siteForm, service_requirements: e.target.value })}
                        placeholder="e.g. Tail-lift required, deliveries before 11am, PPE on site, signed POD."
                        className="mt-1 w-full rounded-sm border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900"
                      />
                    </div>

                    {/* Drops — a site can have multiple drop-off addresses that
                        auto-fill the Job form when picked. */}
                    <div className="md:col-span-2 rounded-sm border border-slate-200 bg-slate-50/60 p-3" data-testid="site-routes-block">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <Label className="text-sm font-medium text-slate-900">Drops</Label>
                          <p className="text-xs text-slate-500 mt-0.5">Add each drop-off for this site. On a new job they'll auto-fill as Drop 1, Drop 2, Drop 3…</p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          data-testid="site-route-add"
                          onClick={() => setSiteForm({
                            ...siteForm,
                            routes: [...(siteForm.routes || []), { dropoff_address: "" }],
                          })}
                          className="rounded-sm h-7 text-xs"
                        >
                          + Add drop
                        </Button>
                      </div>
                      {(siteForm.routes || []).length === 0 ? (
                        <div className="text-xs text-slate-500 italic py-1">No drops yet.</div>
                      ) : (
                        <div className="space-y-2">
                          {(siteForm.routes || []).map((r, idx) => (
                            <div key={idx} className="flex items-center gap-2" data-testid={`site-route-row-${idx}`}>
                              <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 w-16 shrink-0">Drop {idx + 1}</span>
                              <Input
                                data-testid={`site-route-dropoff-${idx}`}
                                value={r.dropoff_address || ""}
                                onChange={(e) => {
                                  const next = [...siteForm.routes];
                                  next[idx] = { ...next[idx], dropoff_address: e.target.value };
                                  setSiteForm({ ...siteForm, routes: next });
                                }}
                                placeholder={`Drop ${idx + 1} address`}
                                className="flex-1 h-8 rounded-sm border-slate-300 text-sm"
                              />
                              <button
                                type="button"
                                data-testid={`site-route-remove-${idx}`}
                                onClick={() => setSiteForm({
                                  ...siteForm,
                                  routes: siteForm.routes.filter((_, i) => i !== idx),
                                })}
                                className="h-8 w-8 inline-flex items-center justify-center rounded-sm text-red-600 hover:bg-red-50"
                                title="Remove"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <FieldI label="Notes" testid="site-notes" value={siteForm.notes} onChange={(v) => setSiteForm({ ...siteForm, notes: v })} className="md:col-span-2" />
                    {editingSite && (
                      <div className="md:col-span-2 pt-2 border-t border-slate-100">
                        <FileUploader entityType="site" entityId={editingSite.id} label="Site documents" />
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" className="rounded-sm" onClick={() => setSiteOpen(false)}>Cancel</Button>
                    <Button data-testid="site-save-btn" onClick={saveSite} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">Save</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 uppercase text-xs tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3">Site</th>
                  <th className="text-left px-5 py-3">Address</th>
                  <th className="text-left px-5 py-3">Contact</th>
                  <th className="text-right px-5 py-3">Miles</th>
                  <th className="text-right px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sites.length === 0 ? (
                  <tr><td colSpan={5} className="text-center text-slate-500 py-12">
                    <MapPin className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                    <div className="font-medium text-slate-700">No sites yet</div>
                    <div className="text-xs">Add the first delivery site for {client.name}.</div>
                  </td></tr>
                ) : sites.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100" data-testid={`site-row-${s.id}`}>
                    <td className="px-5 py-3 font-medium">{s.name}</td>
                    <td className="px-5 py-3 text-slate-600">
                      {[s.address_line1, s.city, s.postcode].filter(Boolean).join(", ") || "—"}
                    </td>
                    <td className="px-5 py-3 text-slate-600">
                      {s.contact_name ? `${s.contact_name}${s.contact_phone ? ` · ${s.contact_phone}` : ""}` : "—"}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums text-slate-700" data-testid={`site-miles-${s.id}`}>
                      {s.miles ? `${s.miles} mi` : "—"}
                      {(s.routes || []).length > 0 && (
                        <div className="text-[10px] text-slate-500 font-normal mt-0.5">
                          {(s.routes || []).length} drop{(s.routes || []).length === 1 ? "" : "s"}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <Button variant="ghost" size="sm" className="h-8" onClick={() => openEditSite(s)} data-testid={`site-edit-${s.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 text-red-600 hover:text-red-700" onClick={() => removeSite(s)} data-testid={`site-del-${s.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* ASSIGNMENTS TAB */}
        <TabsContent value="assignments" className="mt-4">
          <Card className="rounded-sm border border-slate-200 shadow-none">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="text-sm text-slate-600">Map subcontractors to sites. Jobs are auto-routed using these rules.</div>
              <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-assign-btn" onClick={openCreateAssign} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
                    <Plus className="h-4 w-4 mr-2" /> Add Assignment
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl rounded-sm">
                  <DialogHeader>
                    <DialogTitle>{editingAssign ? "Edit Assignment" : "New Subcontractor Assignment"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-2">
                    <div>
                      <Label className="text-xs text-slate-600">Subcontractor</Label>
                      <Select value={assignForm.subcontractor_id || undefined} onValueChange={(v) => setAssignForm({ ...assignForm, subcontractor_id: v })}>
                        <SelectTrigger data-testid="assign-sub" className="mt-1 rounded-sm border-slate-300">
                          <SelectValue placeholder="Choose a subcontractor" />
                        </SelectTrigger>
                        <SelectContent>
                          {subs.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                      {subs.length === 0 && (
                        <div className="text-xs text-amber-600 mt-1">
                          No subcontractors found. Add one in <Link to="/contacts" className="underline">Contacts</Link> first.
                        </div>
                      )}
                    </div>

                    <div>
                      <Label className="text-xs text-slate-600">Scope</Label>
                      <Select value={assignForm.scope} onValueChange={(v) => setAssignForm({ ...assignForm, scope: v, site_ids: v === "all" ? [] : assignForm.site_ids })}>
                        <SelectTrigger data-testid="assign-scope" className="mt-1 rounded-sm border-slate-300">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All sites of {client.name}</SelectItem>
                          <SelectItem value="specific">Specific site(s)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {assignForm.scope === "specific" && (
                      <div>
                        <Label className="text-xs text-slate-600 mb-2 block">Pick sites</Label>
                        {sites.length === 0 ? (
                          <div className="text-xs text-slate-500">Add sites first.</div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto p-3 bg-slate-50 rounded-sm border border-slate-200">
                            {sites.map((s) => (
                              <label key={s.id} className="inline-flex items-center gap-2 text-sm cursor-pointer">
                                <Checkbox
                                  data-testid={`assign-site-${s.id}`}
                                  checked={assignForm.site_ids.includes(s.id)}
                                  onCheckedChange={() => toggleSiteInAssign(s.id)}
                                />
                                <span>{s.name}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between bg-slate-50 rounded-sm p-3 border border-slate-200">
                      <div>
                        <div className="text-sm font-medium text-slate-900">Default subcontractor</div>
                        <div className="text-xs text-slate-500">
                          Used as a fallback for any site without a specific or &quot;all&quot; rule. Only one allowed per client.
                        </div>
                      </div>
                      <Switch
                        data-testid="assign-default"
                        checked={assignForm.is_default}
                        onCheckedChange={(v) => setAssignForm({ ...assignForm, is_default: v })}
                      />
                    </div>

                    <FieldI label="Notes" testid="assign-notes" value={assignForm.notes} onChange={(v) => setAssignForm({ ...assignForm, notes: v })} />
                  </div>
                  <DialogFooter>
                    <Button variant="outline" className="rounded-sm" onClick={() => setAssignOpen(false)}>Cancel</Button>
                    <Button data-testid="assign-save-btn" onClick={saveAssign} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">Save</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 uppercase text-xs tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3">Subcontractor</th>
                  <th className="text-left px-5 py-3">Scope</th>
                  <th className="text-left px-5 py-3">Default</th>
                  <th className="text-right px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {assignments.length === 0 ? (
                  <tr><td colSpan={4} className="text-center text-slate-500 py-12">
                    <UserCheck className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                    <div className="font-medium text-slate-700">No assignments yet</div>
                    <div className="text-xs">Map a subcontractor to one, multiple, or all sites.</div>
                  </td></tr>
                ) : assignments.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100 align-top" data-testid={`assign-row-${a.id}`}>
                    <td className="px-5 py-3 font-medium">{subName(a.subcontractor_id)}</td>
                    <td className="px-5 py-3 text-slate-600">
                      {a.scope === "all" ? (
                        <Badge variant="secondary" className="rounded-sm">All sites</Badge>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {a.site_ids.map((sid) => (
                            <Badge key={sid} variant="outline" className="rounded-sm">{siteName(sid)}</Badge>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {a.is_default ? <Badge className="rounded-sm bg-emerald-600 hover:bg-emerald-700">Default</Badge> : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <Button variant="ghost" size="sm" className="h-8" onClick={() => openEditAssign(a)} data-testid={`assign-edit-${a.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 text-red-600 hover:text-red-700" onClick={() => removeAssign(a)} data-testid={`assign-del-${a.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function FieldI({ label, value, onChange, testid, type = "text", className = "", required, ...rest }) {
  return (
    <div className={className}>
      <Label className="text-xs text-slate-600">{label}{required && <span className="text-red-500"> *</span>}</Label>
      <Input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1 rounded-sm border-slate-300" {...rest} />
    </div>
  );
}
